import { Tabs, TabsType } from "@seiue/ui"
import { Result } from "antd"
import React, { useMemo } from "react"
import {
  match as MatchProp,
  matchPath,
  useHistory,
  useParams,
} from "react-router-dom"
import styled, { css } from "styled-components"

import { RouteNode } from "packages/route"
import { NestedRoute } from "packages/route/components/NestedRoute"

/**
 * 记忆上一次渲染 RouteTabs 时的 pathname,
 * 如果这一次也通过父路由 (未指定具体标签) 访问同样的 tab 组,
 * 则沿用上一次的 active tab, 而非 fallback 至第一个 tab.
 *
 * 需求场景举例:
 * 老师在侧边栏中切换课程班时, 每个课程班会继承上一个课程班的 active tab,
 * 以方便老师对比同一类数据 (比如考勤统计) 或进行同一类操作 (比如录入成绩).
 */
let lastPathname = ""

/**
 * 用于将子路由 (route.subRoutes) 渲染为页签, 同子路由一样可嵌套
 *
 * @param param - assignment object
 * @param param.routes - 路由信息
 * @param param.routesFilter - 不传则不 filter，传则只显示按 index 对应 true 的 route
 * @param param.replace - 是否进行路由替换
 * @param param.contentStyles - 内容 block 的样式
 * @param param.tabBarStyle - rabBar 的样式
 * @param param.style - container 样式
 * @param param.tabsType - tabs 的类型，主要是一些样式上的差别
 * @param param.hideTabBarBottomBorder - 是否隐藏 TabBar 底部 border
 * @param param.onChange - 变化的回调
 * @param param.onActiveRouteChange - active 路由变化回调，可获取到 active 路由信息
 * @param param.tabRender - 替换 tab bar，用于二次封装标签头
 * @param param.tabBarExtraContent - tab bar 上额外的元素
 * @param param.params - 额外自由参数，透传给子路由
 * @returns React.ReactElement
 */
export const RouteTabs: React.FC<
  // 支持额外的、透传给子路由的自由参数
  { [key: string]: any } & {
    routes?: RouteNode[];

    // 不传则不 filter，传则只显示按 index 对应 true 的 route
    routesFilter?: boolean[];
    replace?: boolean;
    match?: MatchProp;
    style?: React.CSSProperties;
    tabBarStyle?: React.CSSProperties;
    tabBarClassName?: string;
    tabRender?: (child: RouteNode, index: number) => React.ReactNode;
    // tab bar 上额外的元素，一个元素时默认居右
    tabBarExtraContent?:
      | React.ReactNode
      | { left?: React.ReactNode; right?: React.ReactNode };
    hideTabBarBottomBorder?: boolean;
    contentStyles?: React.CSSProperties;
    tabsType?: TabsType;
    onChange?: (activeKey: string) => void;
    onActiveRouteChange?: (route: RouteNode) => void;
  }
> = ({
  routes = [],
  routesFilter,
  replace = true,
  contentStyles,
  tabBarStyle,
  style,
  tabsType = "card",
  hideTabBarBottomBorder,
  onChange: outerOnChange,
  onActiveRouteChange,
  tabRender,
  tabBarExtraContent,
  // 额外自由参数，透传给子路由
  ...params
}) => {
  const history = useHistory()
  const { pathname } = history.location
  const routeParams = useParams<any>()

  // 用 routesFilter 和 authRoute 过滤
  const finalRoutes = useMemo(
    () => routes.filter((rt, idx) => !routesFilter || routesFilter[idx]),
    [routes, routesFilter]
  )

  // 将当前路由参数赋给子路由, 得到真实路径
  const tabRoutes = finalRoutes.map((route) => ({
    route,
    realPath: Object.keys(routeParams).reduce(
      (res, key) => res.replace(`:${key}`, routeParams[key]),
      route.path
    ),
  }))

  /**
   * 根据当前 url 计算 active tab
   */
  const activeTabRoute = tabRoutes.find(
    ({ route: { path } }) => !!matchPath(pathname, path)
  )

  /**
   * 根据 lastPathname 计算若 activeTabRoute 未命中时的备用 active tab.
   *
   * 当 activeTabRoute 未命中时, 说明访问的是父路由, 未指定子路由,
   * 则尝试利用 lastPathname fallback 到上一次的 active tab.
   *
   * 若 lastPathname 未命中, 说明本次与上次访问的不是同一套 tab,
   * 则 fallback 到第一个 tab, 并更新已失效的 lastPathname
   */
  const fallbackTabRoute =
    tabRoutes.find(({ route: { path } }) => !!matchPath(lastPathname, path)) ??
    tabRoutes[0]

  /**
   * 当最终的 active tab 不 match lastPathname 时,
   * 说明用户已跳转到其他页签, 则更新 lastPathname
   */
  if (
    (activeTabRoute ?? fallbackTabRoute) &&
    !matchPath(lastPathname, (activeTabRoute ?? fallbackTabRoute).route.path)
  ) {
    lastPathname = pathname
  }

  React.useEffect(() => {
    if (!activeTabRoute || !onActiveRouteChange) return
    onActiveRouteChange(activeTabRoute.route)
  }, [activeTabRoute, onActiveRouteChange])

  const onChange = replace ? history.replace : history.push

  return (
    <TabsContainer tabsType={tabsType} className="sei-route-tab" style={style}>
      {finalRoutes.length ? (
        <>
          <Tabs
            tabBarStyle={{
              ...tabBarStyle,
              position: "relative",
            }}
            hideTabBarBottomBorder={hideTabBarBottomBorder}
            type={tabsType}
            activeKey={activeTabRoute?.realPath}
            onChange={outerOnChange || onChange}
            tabBarExtraContent={tabBarExtraContent}
          >
            {finalRoutes.map((child, index) => (
              <Tabs.TabPane
                key={tabRoutes[index].realPath}
                tab={tabRender?.(child, index) || child.getTitle()}
              />
            ))}
          </Tabs>
          <TabContent
            tabsType={tabsType}
            style={contentStyles}
            data-test-id={`${activeTabRoute?.route.getTitle()}页签`}
          >
            <NestedRoute
              routes={finalRoutes}
              redirect={{
                to: fallbackTabRoute.realPath,
              }}
              {...params}
            />
          </TabContent>
        </>
      ) : (
        <Result status={404} title="404" subTitle={"暂无内容"} />
      )}
    </TabsContainer>
  )
}

const TabsContainer = styled.div<{ tabsType: TabsType }>`
  width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;

  ${(props) =>
    props.tabsType === "card" &&
    css`
      background-color: #ffffff;
      border-radius: 6px;
    `};

  .se-tabs_hide-bottom-border {
    .ant-tabs-nav::before {
      border-bottom: none;
    }
  }
`

const TabContent = styled.div<{ tabsType: TabsType }>`
  ${(props) =>
    props.tabsType === "card" &&
    css`
      margin-top: -40px;
      padding: 8px 24px 24px 24px;
    `};
`
